import React, { useEffect, useState } from 'react';
import { Bus, Train, Ship, Loader2, X, Plus, ArrowRight, Sparkles } from 'lucide-react';
import { useTranslation } from '../../useTranslation';
import CityInput from '../../CityInput';
import { TagCombobox } from '../common/TagCombobox';
import { getAllTripTags } from '../../services/api/flightsApi';
import { getDistanceByCities } from '../../services/api/geocodingApi';
import { lookupGroundTrip, parseGroundTicket } from '../../services/api/groundTripsApi';
import type { GroundMode, GroundTrip } from '../../types/ground';
import { TimeHmInputs } from '../flights/flightFormUtils';
import {
  THEMES,
  minutesToFormatted,
  formattedToMinutes,
} from './groundTripUtils';

export const GroundTripForm: React.FC<{
  mode: GroundMode;
  open: boolean;
  onClose: () => void;
  draft: Partial<GroundTrip>;
  setDraft: React.Dispatch<React.SetStateAction<Partial<GroundTrip>>>;
  onSave: () => Promise<void> | void;
  isEditing?: boolean;
  language: string;
  currency?: string;
}> = ({ mode, open, onClose, draft, setDraft, onSave, isEditing, language, currency = '₽' }) => {
  const { t } = useTranslation(language);
  const theme = THEMES[mode];
  const Icon = mode === 'bus' ? Bus : mode === 'ferry' ? Ship : Train;
  const [tags, setTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [calc, setCalc] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [parseMsg, setParseMsg] = useState<string | null>(null);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, boolean>>({});
  const ticketInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    getAllTripTags().then(setTags).catch(() => setTags([]));
    setParseMsg(null);
    setParseErr(null);
  }, [open, mode]);

  // New trip only: auto-fill duration from times (never overwrite on edit).
  useEffect(() => {
    if (!open || isEditing || mode !== 'train') return;
    const dep = (draft.departure_time || '').trim();
    const arr = (draft.arrival_time || '').trim();
    const depParts = dep.match(/^(\d{1,2}):(\d{2})$/);
    const arrParts = arr.match(/^(\d{1,2}):(\d{2})$/);
    if (!depParts || !arrParts) return;
    let mins =
      Number(arrParts[1]) * 60 +
      Number(arrParts[2]) -
      (Number(depParts[1]) * 60 + Number(depParts[2]));
    if (mins < 0) mins += 24 * 60;
    if (mins <= 0) return;
    const formatted = minutesToFormatted(mins);
    setDraft(prev => {
      if (prev.formattedDuration?.trim() && prev.duration != null) return prev;
      if (prev.duration === mins && prev.formattedDuration === formatted) return prev;
      return { ...prev, duration: mins, formattedDuration: formatted };
    });
  }, [open, isEditing, mode, draft.departure_time, draft.arrival_time, setDraft]);

  if (!open) return null;

  const labelCl = 'block text-xs font-medium text-stone-600 dark:text-slate-300 mb-1';
  const inputCl =
    'w-full rounded-xl border border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-stone-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-offset-0 ' +
    (mode === 'bus'
      ? 'focus:ring-violet-400/60'
      : mode === 'ferry'
        ? 'focus:ring-cyan-400/60'
        : 'focus:ring-indigo-400/60');
  const bad = (k: string) => (invalid[k] ? ' border-red-400' : '');

  const onTicketFile = async (file: File | null) => {
    if (!file) return;
    setParsing(true);
    setParseMsg(null);
    setParseErr(null);
    try {
      const data = await parseGroundTicket(file, mode, language);
      setDraft(prev => ({
        ...prev,
        date: data.date || prev.date,
        originCity: data.originCity || prev.originCity,
        destinationCity: data.destinationCity || prev.destinationCity,
        origin: data.origin || prev.origin,
        destination: data.destination || prev.destination,
        departure_time: data.departure_time || prev.departure_time,
        arrival_time: data.arrival_time || prev.arrival_time,
        operator: data.operator ?? prev.operator,
        routeNumber: data.routeNumber ?? prev.routeNumber,
        trainName: data.trainName ?? prev.trainName,
        distance: data.distance ?? prev.distance,
        duration: data.duration ?? prev.duration,
        formattedDuration: data.formattedDuration || prev.formattedDuration,
        price: data.price ?? prev.price,
        notes: data.notes ?? prev.notes,
        originSubdivision: data.originSubdivision ?? prev.originSubdivision,
        destinationSubdivision: data.destinationSubdivision ?? prev.destinationSubdivision,
      }));
      setParseMsg(t.ticketParsed || t.boardingPassParsed);
    } catch (e) {
      setParseErr(e instanceof Error ? e.message : (t.ticketParseFailed || t.boardingPassParseFailed));
    } finally {
      setParsing(false);
      if (ticketInputRef.current) ticketInputRef.current.value = '';
    }
  };

  const handleLookup = async () => {
    if (!draft.routeNumber?.trim() || !draft.date) {
      setParseErr(language.startsWith('ru') ? 'Укажите дату и номер поезда' : 'Enter date and train number');
      return;
    }
    setLookupLoading(true);
    setParseMsg(null);
    setParseErr(null);
    try {
      const data = await lookupGroundTrip(draft.routeNumber.trim(), draft.date, language, {
        originCity: draft.originCity,
        destinationCity: draft.destinationCity,
      });
      setDraft(prev => ({
        ...prev,
        origin: data.origin || prev.origin,
        destination: data.destination || prev.destination,
        // «Получить данные» always applies official timetable labels/times/distance
        originCity: data.originCity || prev.originCity,
        destinationCity: data.destinationCity || prev.destinationCity,
        operator: data.operator || prev.operator || '',
        routeNumber: data.routeNumber || prev.routeNumber,
        trainName: data.trainName || prev.trainName || '',
        trainNameEn: data.trainNameEn || prev.trainNameEn || '',
        departure_time: data.departure_time || prev.departure_time || '',
        arrival_time: data.arrival_time || prev.arrival_time || '',
        duration: data.duration ?? prev.duration ?? undefined,
        formattedDuration: data.formattedDuration || prev.formattedDuration || '',
        distance:
          data.distance != null && data.distance > 0
            ? data.distance
            : prev.distance,
        origin_station_info: data.origin_station_info || prev.origin_station_info,
        destination_station_info: data.destination_station_info || prev.destination_station_info,
      }));
      setParseMsg(
        language.startsWith('ru')
          ? 'Данные поезда подставлены — проверьте маршрут'
          : 'Train data filled — please verify the route',
      );
    } catch (e) {
      setParseErr(
        e instanceof Error
          ? e.message
          : language.startsWith('ru')
            ? 'Поезд не найден'
            : 'Train not found',
      );
    } finally {
      setLookupLoading(false);
    }
  };

  const calcDistance = async () => {
    if (!draft.originCity?.trim() || !draft.destinationCity?.trim()) return;
    setCalc(true);
    try {
      const res = await getDistanceByCities(draft.originCity.trim(), draft.destinationCity.trim());
      const km = Math.round(Number(res?.distance ?? res?.distance_km ?? 0));
      if (km > 0) setDraft(prev => ({ ...prev, distance: km }));
      if (!isEditing) {
        const sec = Number(res?.duration ?? res?.duration_seconds ?? 0);
        if (sec > 0) {
          const mins = Math.round(sec / 60);
          setDraft(prev => ({ ...prev, duration: mins, formattedDuration: minutesToFormatted(mins) }));
        }
      }
    } catch {
      /* ignore */
    } finally {
      setCalc(false);
    }
  };

  const handleSave = async () => {
    const nextInvalid: Record<string, boolean> = {};
    if (!draft.date) nextInvalid.date = true;
    if (!draft.originCity?.trim()) nextInvalid.originCity = true;
    if (!draft.destinationCity?.trim()) nextInvalid.destinationCity = true;
    setInvalid(nextInvalid);
    if (Object.keys(nextInvalid).length) return;
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  };

  const title = isEditing
    ? mode === 'bus'
      ? t.editBusTrip || t.editTrip
      : mode === 'ferry'
        ? t.editFerryTrip || t.editTrip
        : t.editTrainTrip || t.editTrip
    : mode === 'bus'
      ? t.addBusTrip || t.addTrip
      : mode === 'ferry'
        ? t.addFerryTrip || t.addTrip
        : t.addTrainTrip || t.addTrip;

  const ticketHint =
    mode === 'bus'
      ? (t.ticketParseHintBus || t.ticketParseHint || 'Фото или PDF билета автобуса')
      : mode === 'ferry'
        ? (t.ticketParseHintFerry || t.ticketParseHint || 'Фото или PDF билета парома')
        : (t.ticketParseHintTrain || t.ticketParseHint || 'Фото или PDF билета поезда');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
      <div
        className={`relative w-full sm:max-w-xl max-h-[92dvh] overflow-visible sm:overflow-hidden rounded-2xl border shadow-xl flex flex-col ${theme.panel}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-stone-200/70 dark:border-slate-700/70">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`flex h-8 w-8 items-center justify-center rounded-xl text-white ${theme.iconBg}`}>
              <Icon className="h-4 w-4" />
            </span>
            <h2 className="text-base font-semibold truncate text-stone-900 dark:text-white">{title}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-3 space-y-3 flex-1 overflow-x-visible">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={ticketInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={e => void onTicketFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              onClick={() => ticketInputRef.current?.click()}
              disabled={parsing}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white shadow-sm ${theme.addBtn} disabled:opacity-60`}
              title={ticketHint}
            >
              {parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
              {ticketHint}
            </button>
            {mode === 'train' && (
              <button
                type="button"
                onClick={() => void handleLookup()}
                disabled={lookupLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-60"
              >
                {lookupLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {t.getFlightData}
              </button>
            )}
            {parseMsg && <span className="text-xs text-emerald-600 dark:text-emerald-400">{parseMsg}</span>}
            {parseErr && <span className="text-xs text-red-600 dark:text-red-400">{parseErr}</span>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCl}>{t.date} *</label>
              <input
                type="date"
                value={draft.date || ''}
                onChange={e => setDraft(p => ({ ...p, date: e.target.value }))}
                className={inputCl + bad('date')}
              />
            </div>
            <div>
              <label className={labelCl}>{t.tagSelectOrCreate || t.tag} *</label>
              <TagCombobox
                value={draft.tag || ''}
                onChange={v => setDraft(p => ({ ...p, tag: v }))}
                tags={tags}
                placeholder={t.tagSelectOrCreate || t.tag}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <CityInput
                label={mode === 'train' ? t.originStation : t.originCity}
                value={draft.originCity || ''}
                onChange={v => setDraft(p => ({ ...p, originCity: v }))}
                language={language}
                compact
                required
                isInvalid={!!invalid.originCity}
              />
            </div>
            <div>
              <CityInput
                label={mode === 'train' ? t.destinationStation : t.destinationCity}
                value={draft.destinationCity || ''}
                onChange={v => setDraft(p => ({ ...p, destinationCity: v }))}
                language={language}
                compact
                required
                isInvalid={!!invalid.destinationCity}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCl}>
                {mode === 'bus' ? t.busOperator : mode === 'ferry' ? t.ferryOperator : t.trainOperator}
              </label>
              <input
                type="text"
                value={draft.operator || ''}
                onChange={e => setDraft(p => ({ ...p, operator: e.target.value }))}
                className={inputCl}
                placeholder={mode === 'bus' ? 'FlixBus' : mode === 'ferry' ? 'Stena Line' : 'ФПК'}
              />
            </div>
            <div>
              <label className={labelCl}>
                {mode === 'bus' ? t.busNumber : mode === 'ferry' ? t.ferryNumber : t.trainNumber}
              </label>
              <input
                type="text"
                value={draft.routeNumber || ''}
                onChange={e => setDraft(p => ({ ...p, routeNumber: e.target.value }))}
                className={inputCl}
                placeholder={mode === 'bus' ? 'N12' : mode === 'ferry' ? 'SF123' : '072'}
              />
            </div>
          </div>

          {mode === 'train' && (
            <div>
              <label className={labelCl}>{t.trainBrandedName}</label>
              <input
                type="text"
                value={draft.trainName || ''}
                onChange={e => setDraft(p => ({ ...p, trainName: e.target.value }))}
                className={inputCl}
                placeholder={
                  language.startsWith('ru')
                    ? 'Высокоскоростной поезд «Фусин»'
                    : 'Fuxing high-speed train'
                }
              />
            </div>
          )}

          {mode === 'train' ? (
            <div className="grid grid-cols-3 gap-x-1.5 sm:gap-x-2 gap-y-1.5">
              <div className="min-w-0">
                <label className={labelCl} title={t.trainDepartureTime}>{t.trainDepartureTime}</label>
                <TimeHmInputs
                  value={draft.departure_time || ''}
                  onChange={v => setDraft(prev => ({ ...prev, departure_time: v }))}
                />
              </div>
              <div className="min-w-0">
                <label className={labelCl} title={t.trainArrivalTime}>{t.trainArrivalTime}</label>
                <TimeHmInputs
                  value={draft.arrival_time || ''}
                  onChange={v => setDraft(prev => ({ ...prev, arrival_time: v }))}
                />
              </div>
              <div className="min-w-0">
                <label className={labelCl} title={t.duration}>{t.duration}</label>
                <TimeHmInputs
                  value={draft.formattedDuration || ''}
                  onChange={v =>
                    setDraft(prev => ({
                      ...prev,
                      formattedDuration: v,
                      duration: formattedToMinutes(v),
                    }))
                  }
                />
              </div>
            </div>
          ) : null}

          <div className={`grid gap-3 ${mode === 'train' ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <div>
              <label className={labelCl}>{t.distance}</label>
              {mode === 'train' ? (
                <input
                  type="number"
                  min={0}
                  value={draft.distance ?? ''}
                  onChange={e => setDraft(p => ({ ...p, distance: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  className={inputCl}
                />
              ) : (
                <div className="flex gap-1">
                  <input
                    type="number"
                    min={0}
                    value={draft.distance ?? ''}
                    onChange={e => setDraft(p => ({ ...p, distance: e.target.value === '' ? undefined : Number(e.target.value) }))}
                    className={inputCl}
                  />
                  <button
                    type="button"
                    onClick={() => void calcDistance()}
                    disabled={calc}
                    className="shrink-0 px-2 rounded-xl border border-stone-200 dark:border-slate-600 text-xs hover:bg-stone-50 dark:hover:bg-slate-700"
                    title={t.calculatingDistance}
                  >
                    {calc ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </div>
            {mode !== 'train' && (
              <div>
                <label className={labelCl}>{t.duration}</label>
                <input
                  type="text"
                  value={draft.formattedDuration || ''}
                  onChange={e => {
                    const formattedDuration = e.target.value;
                    setDraft(p => ({
                      ...p,
                      formattedDuration,
                      duration: formattedToMinutes(formattedDuration),
                    }));
                  }}
                  className={inputCl}
                  placeholder="5:30"
                />
              </div>
            )}
            <div>
              <label className={labelCl}>{t.cost}</label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={draft.price ?? ''}
                onChange={e => setDraft(p => ({ ...p, price: e.target.value === '' ? undefined : Number(e.target.value) }))}
                className={inputCl}
                placeholder={currency}
              />
            </div>
          </div>

          {mode !== 'train' && (
            <div>
              <label className={labelCl}>{t.notes || (language.startsWith('ru') ? 'Заметки' : 'Notes')}</label>
              <textarea
                rows={2}
                value={draft.notes || ''}
                onChange={e => setDraft(p => ({ ...p, notes: e.target.value }))}
                className={inputCl + ' resize-none'}
              />
            </div>
          )}
        </div>

        <div className="shrink-0 px-4 py-3 border-t border-stone-200/70 dark:border-slate-700/70 flex justify-end">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-md ${theme.addBtn}`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {isEditing ? t.saveChanges : title}
          </button>
        </div>
      </div>
    </div>
  );
};
